// Inicialização: sessão -> empresa -> fila pendente -> dados -> legado -> tempo real.
import { supabase, isConfigured } from './services/supabase.js';
import { auth } from './services/authService.js';
import { loadWorkspace, rpc } from './services/databaseService.js';
import { createPhotos, uploadPhoto } from './services/storageService.js';
import { createSync } from './services/syncService.js';
import { logoMediaId } from './services/mappers.js';
import { screen, closeScreen, h, field, errorText } from './components/ui.js';
import { showLogin, showNewPassword } from './modules/authScreens.js';
import { openWizard } from './modules/migrationWizard.js';
import { mountAccountBar, showStatus } from './modules/account.js';

let started = false, recovering = false;
const clientId = sessionStorage.getItem('pm_client') || crypto.randomUUID();
sessionStorage.setItem('pm_client', clientId);

async function resolveLogo(entries, photos) {
  const s = JSON.parse(entries.print3d_settings);
  const m = /^media:(.+)$/.exec(s.systemIdentity?.logo || '');
  if (m) { s.systemIdentity.logo = await photos.load(m[1]); entries.print3d_settings = JSON.stringify(s); }
}

async function chooseWorkspace(user) {
  await rpc('accept_my_invites', {});
  const { data, error } = await supabase.from('workspace_members').select('role, workspaces(id,name)').eq('user_id', user.id);
  if (error) throw error;
  if (data.length) return { id: data[0].workspaces.id, name: data[0].workspaces.name, role: data[0].role };
  return new Promise(resolve => {
    const name = h('input', { type: 'text', required: true, value: 'Dream Studio 3D' }), msg = h('p', { class: 'pc-msg err' });
    screen('Configurar empresa', 'Nenhuma empresa encontrada nesta conta.',
      h('form', { onsubmit: async e => {
        e.preventDefault();
        try { const id = await rpc('create_first_workspace', { p_name: name.value.trim() }); resolve({ id, name: name.value.trim(), role: 'owner' }); }
        catch (err) { msg.textContent = errorText(err); }
      } }, field('Nome da empresa', name), msg, h('button', { class: 'btn btn-primary pc-wide', type: 'submit' }, 'Criar empresa')));
  });
}

async function start(session) {
  if (started) return; started = true;
  screen('Carregando', 'Sincronizando dados da empresa...');
  try {
    const ws = await chooseWorkspace(session.user);
    const qKey = 'pm_outbox_' + ws.id;
    const persist = { load: () => JSON.parse(localStorage.getItem(qKey) || '[]'), save: q => localStorage.setItem(qKey, JSON.stringify(q)) };
    let statusEl = null, photos = null, hadData = false;
    const api = {
      applyChangeset: (id, client, ops, movs) => rpc('apply_changeset', { p_ws: ws.id, p_changeset: id, p_client: client, p_ops: ops, p_movements: movs }),
      loadAll: async () => { const l = await loadWorkspace(ws.id); photos?.setMedia(l.media); if (photos) await resolveLogo(l.entries, photos); return l; },
    };
    const sync = createSync({
      store: window.PMStore, api, clientId, uuid: () => crypto.randomUUID(), persist,
      onStatus: s => { if (statusEl) showStatus(statusEl, s); },
      onRejected: ({ code, message, changesets }) => {
        localStorage.setItem('pm_rejected_' + Date.now(), JSON.stringify({ code, message, changesets }));  // nada digitado é perdido
        window.PMLegacy.toast(message, 'error');
      },
      beforeDiff: state => { const l = state.settings.systemIdentity?.logo; if (typeof l === 'string' && l.startsWith('data:')) photos.save(logoMediaId(l), l); },
      reload: () => window.PMLegacy.rerender(),
    });
    await sync.flush();  // envia o que ficou pendente de uma sessão anterior antes de ler o servidor
    const loaded = await loadWorkspace(ws.id);
    photos = createPhotos(ws.id, loaded.media, e => console.warn('Foto pendente de envio', e));
    await resolveLogo(loaded.entries, photos);
    hadData = JSON.parse(loaded.entries.print3d_products).length > 0;
    window.PMStore.loadSilently(loaded.entries);
    sync.setVersions(loaded.versions);

    const openImport = (mode, file) => openWizard({
      ws: ws.id, mode, file,
      api: { rpc, uploadPhoto, hadData, reload: async () => { const l = await api.loadAll(); return l; } },
      onDone: () => sync.refresh(),
    });
    window.PMCloud = {
      ws: ws.id, photos,
      openRestore: file => { if (ws.role !== 'owner') return window.PMLegacy.toast('Apenas o proprietário restaura backups.', 'error'); openImport('replace', file); },
      logBackup: kind => rpc('log_backup', { p_ws: ws.id, p_kind: kind, p_counts: {} }).catch(e => console.warn(e)),
    };
    await window.PMLegacy.init();
    sync.rebase();
    window.PMStore.onSave(() => sync.capture());

    statusEl = mountAccountBar({ user: session.user, role: ws.role, wsName: ws.name, onMigrate: () => openImport('merge'), onLogout: async () => { if (sync.pending && !confirm('Há alterações ainda não enviadas. Sair mesmo assim? Elas continuam guardadas neste navegador.')) return; await auth.signOut(); location.reload(); } });
    closeScreen();

    let timer; const scheduleRefresh = () => { clearTimeout(timer); timer = setTimeout(() => sync.refresh().catch(e => console.warn(e)), 400); };
    supabase.channel('ws-' + ws.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'changesets', filter: 'workspace_id=eq.' + ws.id }, p => { if (p.new.client_id !== clientId) scheduleRefresh(); })
      .subscribe();
    // Realtime é conveniência: voltar à aba ou reconectar sempre reconcilia.
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { sync.flush(); scheduleRefresh(); } });
    window.addEventListener('online', () => { sync.flush(); photos.retryPending(); scheduleRefresh(); });
    window.addEventListener('beforeunload', e => { if (sync.pending) e.preventDefault(); });
    photos.retryPending();
  } catch (e) {
    started = false; console.error(e);
    screen('Não foi possível abrir o sistema', errorText(e), h('button', { class: 'btn btn-primary', onclick: () => location.reload() }, 'Tentar novamente'));
  }
}

async function main() {
  if (!isConfigured) {
    screen('Configuração pendente', 'Defina VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY. Veja DEPLOY.md.');
    return;
  }
  auth.onChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') { recovering = true; showNewPassword(() => { recovering = false; location.replace('/'); }); return; }
    if (event === 'SIGNED_OUT') { if (started) location.reload(); else showLogin(); return; }
    // Sair do callback antes de chamar o Supabase evita travamento interno do cliente de auth.
    if (session && !recovering && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) setTimeout(() => start(session), 0);
  });
  const s = await auth.session();
  if (!s && !location.hash.includes('type=recovery')) showLogin();
}
main();
